import { baseUrl } from './../../utils/axios'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { handleErrors, formatParams, formatDateWithoutTimezone, getParams } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'

import {
  ICreatePayrollForSingleUserRequest,
  ICreatePayrollsRequest,
  IGetAllPayrollsSingleResponse,
  IGetSinglePayrollReponse,
} from 'types/api/payrollsApi.interface'
import { ICreatePayrollModalState } from 'types/payrolls.interface'
import { toast } from 'react-toastify'

const initialState: IPayrollsSlice = {
  isLoading: false,
  payrolls: [],
  totalCount: 0,
  totalPages: 0,
  singlePayroll: null,
}

interface IPayrollsSlice {
  isLoading: boolean
  payrolls: IGetAllPayrollsSingleResponse[]
  totalCount: number
  totalPages: number
  singlePayroll: IGetSinglePayrollReponse
}

export const getPayrolls = createAsyncThunk(
  'payrolls/getPayrolls',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllPayrollsSingleResponse>> =
        await baseUrl.get('users/payroll/all', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSinglePayroll = createAsyncThunk(
  'payrolls/getSinglePayroll',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IGetSinglePayrollReponse> = await baseUrl.get('users/payroll/' + id)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createPayrollForSingleUser = createAsyncThunk(
  'reports/createPayrollForSingleUser',
  async (data: ICreatePayrollModalState, thunkAPI) => {
    try {
      const req: ICreatePayrollForSingleUserRequest = {
        userId: data.singleUser.value,
        date: formatDateWithoutTimezone(
          new Date(data.year.value, data.month.value, 1).toISOString(),
        ),
        startDay: data.singleUser.startDay,
        endDay: data.singleUser.endDay,
        ...(data.generalAllowanceAmount != null && {
          generalAllowanceAmount: data.generalAllowanceAmount,
        }),
      }
      const resp: AxiosResponse = await baseUrl.post('users/payroll', req)
      await thunkAPI.dispatch(getPayrolls(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createPayrolls = createAsyncThunk(
  'reports/createPayrollForSingleUser',
  async (data: ICreatePayrollModalState, thunkAPI) => {
    try {
      const req: ICreatePayrollsRequest = {
        date: formatDateWithoutTimezone(
          new Date(data.year.value, data.month.value, 1).toISOString(),
        ),
        ...(data.generalAllowanceAmount != null && {
          generalAllowanceAmount: data.generalAllowanceAmount,
        }),
        users: data.users.map((u) => ({
          userId: u.value,
          startDay: u.startDay,
          endDay: u.endDay,
          ...(u.generalAllowanceAmount != null && {
            generalAllowanceAmount: u.generalAllowanceAmount,
          }),
        })),
      }
      const resp: AxiosResponse<string> = await baseUrl.post('users/payroll/all', req)
      await thunkAPI.dispatch(getPayrolls(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deletePayroll = createAsyncThunk(
  'reports/deletePayroll',
  async (ids: number[], thunkAPI) => {
    try {
      await baseUrl.delete('users/payroll/multiple', { data: { ids } })
      await thunkAPI.dispatch(getPayrolls(getParams({ location })))
      return ids
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const payrollsSlice = createSlice({
  name: 'payrolls',
  initialState,
  reducers: {
    clearPayrolls: (state) => {
      state.payrolls = []
      state.totalCount = 0
      state.totalPages = 0
    },
    clearSinglePayroll: (state) => {
      state.singlePayroll = null
    },
  },
  extraReducers: {
    [getPayrolls.pending.type]: (state) => {
      state.isLoading = true
    },
    [getPayrolls.fulfilled.type]: (
      state: IPayrollsSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllPayrollsSingleResponse> },
    ) => {
      state.isLoading = false
      state.payrolls = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getPayrolls.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSinglePayroll.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSinglePayroll.fulfilled.type]: (
      state: IPayrollsSlice,
      { payload }: { payload: IGetSinglePayrollReponse },
    ) => {
      state.isLoading = false
      state.singlePayroll = payload
    },
    [getSinglePayroll.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createPayrolls.pending.type]: (state) => {
      state.isLoading = true
    },
    [createPayrolls.fulfilled.type]: (state: IPayrollsSlice) => {
      state.isLoading = false
    },
    [createPayrolls.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createPayrollForSingleUser.pending.type]: (state) => {
      state.isLoading = true
    },
    [createPayrollForSingleUser.fulfilled.type]: (state: IPayrollsSlice) => {
      state.isLoading = false
    },
    [createPayrollForSingleUser.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deletePayroll.pending.type]: (state) => {
      state.isLoading = true
    },
    [deletePayroll.fulfilled.type]: (state: IPayrollsSlice) => {
      state.isLoading = false
      toast.success('Payroll deleted successfully')
    },
    [deletePayroll.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export default payrollsSlice.reducer
export const { clearPayrolls, clearSinglePayroll } = payrollsSlice.actions
